<script setup lang="ts">
import ConnectionsHero from '../../components/connections-hero.vue'
import ConnectionsLayout from '@/modules/shared/layouts/connections-layout.vue'
import ConnectionGroupCard from '../../components/connection-group-card.vue'
import ConnectionInvestorRow from '../../components/connection-investor-row.vue'
import { useRoute, useRouter } from 'vue-router'
import { useInvestingInvestorStore } from '@/modules/investing/stores/investor-store'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { VButton, VIcon, VLoadingSection, VPopUpModal, VSkeletonBar } from '@/modules/shared/components'
import { useConnectionStore } from '@/modules/connecting/stores/connection-store'
import { useModal } from '@/modules/shared/composables/use-modal'
import { onClickOutside } from '@vueuse/core'
import { isNil } from 'lodash'
import type { Connection, GroupCandidateExpanded, InvestorCandidateExpanded } from '../../types'
import { INVESTMENT_TYPE_LABELS } from '../../types'

import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const connectionStore = useConnectionStore()
const investorStore = useInvestingInvestorStore()
const candidateStore = useCandidateStore()
const { individualId } = route.params as { individualId: string }

const otherFirms = computed(() =>
  (authStore.current_user?.groups || []).filter((g: any) => g.subdomain !== 'connections'),
)

///////////////////////////////////////////////////////////////////////////////
// Constants
///////////////////////////////////////////////////////////////////////////////

const TYPE_LABELS = INVESTMENT_TYPE_LABELS

///////////////////////////////////////////////////////////////////////////////
// Tabs
///////////////////////////////////////////////////////////////////////////////

const tabs = [
  { label: 'Recommended Connections', status: 'matched' },
  { label: 'Approved Connections', status: 'accepted' },
]
const currentIndex = ref(0)
const tabNames = tabs.map((tab) => tab.label)
const selectedTab = computed(() => tabs[currentIndex.value])
const isRecommendedTab = computed(() => currentIndex.value === 0)

///////////////////////////////////////////////////////////////////////////////
// Search & Filter
///////////////////////////////////////////////////////////////////////////////

const searchQuery = ref('')
const showFilterDropdown = ref(false)
const filterDropdownRef = ref<HTMLElement | null>(null)

onClickOutside(filterDropdownRef, () => {
  showFilterDropdown.value = false
})

const filters = reactive({
  type: 'all',
  minInvestment: 'all',
  focus: 'all',
})

const hasActiveFilters = computed(() => {
  return (
    searchQuery.value.trim() !== '' ||
    filters.type !== 'all' ||
    filters.minInvestment !== 'all' ||
    filters.focus !== 'all'
  )
})

const activeFilterCount = computed(() => {
  let count = 0
  if (filters.type !== 'all') count++
  if (filters.minInvestment !== 'all') count++
  if (filters.focus !== 'all') count++
  return count
})

function clearFilters() {
  searchQuery.value = ''
  filters.type = 'all'
  filters.minInvestment = 'all'
  filters.focus = 'all'
}

const availableIndustries = computed(() => {
  const industries = new Set<string>()
  connections.value.forEach((c) => {
    c.candidate_group?.industries_interested?.forEach((i: string) => industries.add(i))
  })
  return Array.from(industries).sort()
})

///////////////////////////////////////////////////////////////////////////////
// Filtered Connections
///////////////////////////////////////////////////////////////////////////////

const connections = computed(() => connectionStore.connections as Connection[])

const filteredConnections = computed(() => {
  return connections.value.filter((connection) => {
    const candidate = connection.candidate_group
    if (!candidate) return false

    // Search filter
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase()
      const searchableText = [
        candidate.subject?.name,
        candidate.investment_thesis,
        ...(candidate.industries_interested || []),
        ...(candidate.investment_types_interested?.map((t) => TYPE_LABELS[t] || t) || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!searchableText.includes(query)) return false
    }

    // Type filter
    if (filters.type !== 'all') {
      const typeLabels = (candidate.investment_types_interested || []).map((t) => TYPE_LABELS[t] || t)
      if (!typeLabels.includes(filters.type)) return false
    }

    // Min investment filter
    if (filters.minInvestment !== 'all') {
      const min = getLowestMin(candidate)
      if (min === null) return false
      switch (filters.minInvestment) {
        case 'under50k':
          if (min >= 50000) return false
          break
        case '50k-100k':
          if (min < 50000 || min > 100000) return false
          break
        case '100k+':
          if (min < 100000) return false
          break
      }
    }

    // Focus/Industry filter
    if (filters.focus !== 'all') {
      if (!(candidate.industries_interested || []).includes(filters.focus)) return false
    }

    return true
  })
})

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const approveLoading = ref<string | null>(null)
const rejectLoading = ref<string | null>(null)

const approveModal = useModal()
const dataInfoModal = useModal()
const pendingApproval = ref<Connection | null>(null)

function requestApproval(connection: Connection) {
  pendingApproval.value = connection
  approveModal.open()
}

function cancelApproval() {
  approveModal.close()
  pendingApproval.value = null
}

async function confirmApproval() {
  if (!pendingApproval.value || approveLoading.value) return
  const connectionId = pendingApproval.value.id
  approveLoading.value = connectionId
  approveModal.close()
  await connectionStore.acceptInvite(connectionId)
  await fetchConnections(selectedTab.value.status)
  approveLoading.value = null
  pendingApproval.value = null
}

const rejectModal = useModal()
const pendingRejection = ref<Connection | null>(null)

function requestRejection(connection: Connection) {
  pendingRejection.value = connection
  rejectModal.open()
}

function cancelRejection() {
  rejectModal.close()
  pendingRejection.value = null
}

async function confirmRejection() {
  if (!pendingRejection.value || rejectLoading.value) return
  const connectionId = pendingRejection.value.id
  rejectLoading.value = connectionId
  rejectModal.close()
  await connectionStore.rejectInvite(connectionId)
  await fetchConnections(selectedTab.value.status)
  rejectLoading.value = null
  pendingRejection.value = null
}

///////////////////////////////////////////////////////////////////////////////
// Helpers
///////////////////////////////////////////////////////////////////////////////

function getInitials(name: string): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getLowestMin(candidate: GroupCandidateExpanded): number | null {
  const mins = [candidate.fund_investment_min, candidate.spv_investment_min, candidate.deal_investment_min].filter(
    (v) => v != null && v > 0,
  )
  return mins.length ? Math.min(...mins) : null
}

function goToFirm(subdomain: string | undefined) {
  if (!subdomain) return
  const baseUrl = import.meta.env.VITE_VENTURE360_REDIRECT_URL
  window.location.href = `${baseUrl}/user?slug=${subdomain}`
}

function goToFirmOrAgreement(connection: any) {
  if (connection.has_pending_agreement) {
    router.push({
      name: 'connecting.agreement',
      params: { slug: 'connections', individualId, connectionId: connection.id },
    })
    return
  }
  goToFirm(connection.candidate_group?.subject?.subdomain)
}

///////////////////////////////////////////////////////////////////////////////
// Data Fetching
///////////////////////////////////////////////////////////////////////////////

const loading = ref(true)
const currentIndividual = computed(() => investorStore.items.get(`individual:${individualId}`))
const myCandidate = computed(() => {
  const candidateId = currentIndividual.value?.connecting_candidate_id
  return candidateId ? candidateStore.getCandidate(candidateId) : undefined
})

const previewInvestorCandidate = computed((): InvestorCandidateExpanded | undefined => {
  const candidate = myCandidate.value
  if (!candidate) return undefined
  const subject = (candidate as any).subject
  if (!subject) return undefined
  return {
    ...candidate,
    subject,
  } as InvestorCandidateExpanded
})

async function fetchConnections(status: string) {
  await connectionStore.fetchConnections({ status, individual_id: individualId })
}

watch(currentIndex, async (newIndex) => {
  loading.value = true
  await fetchConnections(tabs[newIndex].status)
  loading.value = false
})

onMounted(async () => {
  await investorStore.fetchIndividual(individualId)

  if (currentIndividual.value?.connecting_candidate_id) {
    candidateStore.fetchCandidate(currentIndividual.value.connecting_candidate_id)
  }

  await fetchConnections(tabs[0].status)

  if (isNil(currentIndividual.value?.connections_onboarding_completed_at)) {
    router.push({
      name: 'connecting.onboarding',
      params: { individual_id: individualId, slug: 'connections' },
    })
  }
  loading.value = false
})
</script>

<template>
  <ConnectionsLayout>
    <div>
      <!-- Hero Banner -->
      <ConnectionsHero
        title="Your access to top investment managers"
        title-accent="starts here."
        subtitle="We match you with firms that align with your goals based on your investor profile."
      >
        <template #action>
          <div class="flex items-center gap-3">
            <button
              @click="dataInfoModal.open()"
              class="text-sm text-white/70 underline underline-offset-2 hover:text-white"
            >
              What firms can see
            </button>
            <RouterLink
              :to="{
                name: 'connecting.individuals.edit',
                params: { slug: 'connections', individualId },
                query: { mode: 'edit' },
              }"
            >
              <VButton variant="v-blue">
                <span class="flex items-center gap-1.5">
                  <VIcon name="Edit" :size="14" />
                  Edit Profile
                </span>
              </VButton>
            </RouterLink>
          </div>
        </template>
      </ConnectionsHero>

      <!-- Your Firms section -->
      <div v-if="otherFirms.length > 0" class="mb-6">
        <div class="mb-3 flex items-center gap-2">
          <span class="text-sm font-semibold text-gray-500">Your Firms</span>
          <div class="h-px flex-grow bg-gray-200 dark:bg-gray-700"></div>
        </div>
        <div class="flex flex-wrap gap-3">
          <button
            v-for="firm in otherFirms"
            :key="firm.subdomain"
            @click="goToFirm(firm.subdomain)"
            class="firm-card flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3.5 shadow-sm transition-all hover:border-[#3b88af] hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
          >
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#1a2332] to-[#3b88af] text-xs font-bold text-white"
            >
              {{ getInitials(firm.name) }}
            </div>
            <div class="text-left">
              <div class="text-sm font-semibold text-gray-800 dark:text-gray-200">{{ firm.name }}</div>
              <div class="text-[11px] text-gray-400">Go to firm</div>
            </div>
            <VIcon name="ArrowNarrowRight" :size="16" class="ml-2 text-gray-300" />
          </button>
        </div>
      </div>

      <!-- Tab Bar + Search/Filter -->
      <div class="nav-bar">
        <nav class="nav-tabs">
          <a
            v-for="(tab, index) in tabs"
            :key="tab.label"
            :class="{ active: currentIndex === index }"
            @click.prevent="currentIndex = index"
          >
            {{ tab.label }}
          </a>
        </nav>

        <div class="flex items-center gap-2" style="padding-right: 8px">
          <!-- Search -->
          <div class="relative">
            <VIcon name="Search" :size="13" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search..."
              class="w-[180px] rounded-full border-[1.5px] border-gray-200 bg-gray-50 py-[7px] pl-[30px] pr-3 text-[13px] outline-none transition-all focus:border-[#3b88af] focus:bg-white dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:focus:border-[#3b88af]"
            />
          </div>

          <!-- Filter -->
          <div class="relative" ref="filterDropdownRef">
            <button
              @click="showFilterDropdown = !showFilterDropdown"
              class="flex items-center gap-1.5 rounded-full border-[1.5px] border-gray-200 bg-white px-3.5 py-[7px] text-[13px] font-medium text-gray-600 transition-all hover:border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              <VIcon name="FilterLines" :size="13" />
              Filter
              <span
                v-if="activeFilterCount > 0"
                class="rounded-full bg-[#3b88af] px-1.5 py-px text-[10px] font-bold text-white"
              >
                {{ activeFilterCount }}
              </span>
            </button>

            <!-- Filter Dropdown -->
            <div
              v-if="showFilterDropdown"
              class="absolute right-0 top-[calc(100%+8px)] z-[200] min-w-[280px] rounded-lg border-[1.5px] border-gray-200 bg-white p-5 shadow-lg dark:border-gray-600 dark:bg-gray-800"
            >
              <div class="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">Type</div>
              <div class="mb-4 flex flex-wrap gap-1.5">
                <button
                  v-for="opt in ['all', 'Fund', 'Syndicate', 'Angel Group']"
                  :key="opt"
                  @click="filters.type = opt"
                  class="filter-chip"
                  :class="{ active: filters.type === opt }"
                >
                  {{ opt === 'all' ? 'All' : opt }}
                </button>
              </div>

              <div class="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">Minimum Investment</div>
              <div class="mb-4 flex flex-wrap gap-1.5">
                <button
                  v-for="opt in [
                    { value: 'all', label: 'All' },
                    { value: 'under50k', label: 'Under $50K' },
                    { value: '50k-100k', label: '$50K\u2013$100K' },
                    { value: '100k+', label: '$100K+' },
                  ]"
                  :key="opt.value"
                  @click="filters.minInvestment = opt.value"
                  class="filter-chip"
                  :class="{ active: filters.minInvestment === opt.value }"
                >
                  {{ opt.label }}
                </button>
              </div>

              <div class="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">Focus</div>
              <div class="mb-5 flex flex-wrap gap-1.5">
                <button @click="filters.focus = 'all'" class="filter-chip" :class="{ active: filters.focus === 'all' }">
                  All
                </button>
                <button
                  v-for="industry in availableIndustries"
                  :key="industry"
                  @click="filters.focus = industry"
                  class="filter-chip"
                  :class="{ active: filters.focus === industry }"
                >
                  {{ industry }}
                </button>
              </div>

              <button
                @click="clearFilters"
                class="w-full rounded-lg bg-gray-100 py-2 text-[13px] font-medium text-gray-500 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
              >
                Clear all filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab loading spinner -->
      <VLoadingSection v-if="loading" class="py-20" title="Loading…" />

      <!-- Recommended tab: info banner when no connections yet -->
      <div
        v-if="!loading && isRecommendedTab && connections.length === 0"
        class="mb-6 flex gap-3 rounded-lg border border-[#3b88af] bg-[#3b88af]/10 p-5"
      >
        <VIcon name="InfoCircleFilled" :size="40" class="shrink-0 text-[#3b88af]" />
        <div class="flex-grow">
          <div class="my-1 font-semibold dark:text-gray-200">Waiting for Approval</div>
          <div class="text-sm text-gray-600 dark:text-gray-400">
            We've shared your profile with firms that match your interests and preferences. Investment managers review
            and approve connections before you can see their information &mdash; you'll be notified as soon as a firm
            approves you.
          </div>
        </div>
      </div>

      <!-- No filter results -->
      <div
        v-if="!loading && filteredConnections.length === 0 && hasActiveFilters"
        class="py-12 text-center text-sm text-gray-400"
      >
        <VIcon name="Search" :size="36" class="mx-auto mb-3 block text-gray-300" />
        No connections match your filters.
        <button @click="clearFilters" class="ml-1 font-semibold text-[#3b88af]">Clear filters</button>
      </div>

      <!-- Approved tab: empty state with pulse animation -->
      <div
        v-else-if="!loading && !isRecommendedTab && connections.length === 0"
        class="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-md dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="pulse-ring mx-auto mb-6">
          <VIcon name="User" :size="32" class="text-[#3b88af]" />
        </div>
        <div class="mb-2 text-2xl font-semibold text-gray-900 dark:text-gray-200">No approved connections yet</div>
        <p class="mx-auto max-w-[380px] text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Once you approve a connection request, they'll appear here. Go to Recommended Connections to get started.
        </p>
      </div>

      <!-- Card Grid -->
      <TransitionGroup
        v-else-if="!loading"
        tag="div"
        name="card"
        class="grid gap-5"
        style="grid-template-columns: repeat(auto-fill, minmax(300px, 1fr))"
      >
        <ConnectionGroupCard
          v-for="connection in filteredConnections"
          :key="connection.id"
          :candidate="connection.candidate_group"
          interactive
        >
          <template #actions>
            <!-- Actions: Recommended tab -->
            <div v-if="isRecommendedTab" class="mt-4 flex gap-2">
              <VButton
                variant="v-green"
                size="sm"
                class="flex-1"
                @click="requestApproval(connection)"
                :loading="approveLoading === connection.id"
              >
                Approve
              </VButton>
              <button
                @click="requestRejection(connection)"
                class="btn-reject-card flex-1"
                :disabled="rejectLoading === connection.id"
              >
                Reject
              </button>
            </div>

            <!-- Actions: Approved tab -->
            <div v-else class="mt-4 flex items-center gap-2">
              <VButton variant="v-blue" size="sm" class="flex-1" @click="goToFirmOrAgreement(connection)">
                <span class="flex items-center justify-center gap-1.5">
                  <VIcon name="ArrowNarrowRight" :size="14" />
                  Go to Firm
                </span>
              </VButton>
            </div>
          </template>
        </ConnectionGroupCard>

        <!-- Ghost card: "More matches pending..." (Recommended tab only) -->
        <div v-if="isRecommendedTab" key="ghost-card" class="ghost-card">
          <div class="mb-4 flex items-center gap-3">
            <div class="ghost-logo">?</div>
            <div>
              <div class="text-[15px] font-bold text-gray-400">More matches pending…</div>
              <div class="mt-0.5 text-xs text-gray-400">Firms review connections before they become visible to you</div>
            </div>
          </div>
          <div class="mb-3 flex flex-col gap-2">
            <VSkeletonBar width="100%" class="!h-3" />
            <VSkeletonBar width="90%" class="!h-3" />
            <VSkeletonBar width="70%" class="!h-3" />
          </div>
          <VSkeletonBar width="60%" class="!h-4" />
        </div>
      </TransitionGroup>
    </div>

    <!-- Approve Confirmation Modal -->
    <VPopUpModal :modalStore="approveModal">
      <div v-if="pendingApproval" class="text-center">
        <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#3b88af]/10">
          <VIcon name="Check" :size="28" class="text-[#3b88af]" />
        </div>
        <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-gray-200">Approve Connection</h3>
        <p class="mb-6 text-sm leading-relaxed text-gray-500">
          By approving this connection, you'll be added to
          <span class="font-semibold text-gray-700 dark:text-gray-300">
            {{ pendingApproval.candidate_group?.subject?.name }}'s
          </span>
          investor portal.
          {{ pendingApproval.candidate_group?.subject?.name }} can already view your investor profile as part of the
          matching process.
        </p>
        <div class="flex gap-3">
          <button
            @click="cancelApproval"
            class="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <VButton variant="v-green" class="flex-1" @click="confirmApproval" :loading="!!approveLoading">
            Confirm &amp; Connect
          </VButton>
        </div>
      </div>
    </VPopUpModal>

    <!-- Reject Confirmation Modal -->
    <VPopUpModal :modalStore="rejectModal">
      <div v-if="pendingRejection" class="text-center">
        <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <VIcon name="X" :size="28" class="text-red-500" />
        </div>
        <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-gray-200">Reject Connection</h3>
        <p class="mb-6 text-sm leading-relaxed text-gray-500">
          Are you sure you want to reject the connection with
          <span class="font-semibold text-gray-700 dark:text-gray-300">
            {{ pendingRejection.candidate_group?.subject?.name }} </span
          >? This firm will no longer appear in your recommended connections.
        </p>
        <div class="flex gap-3">
          <button
            @click="cancelRejection"
            class="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <VButton variant="v-red" class="flex-1" @click="confirmRejection" :loading="!!rejectLoading">
            Reject
          </VButton>
        </div>
      </div>
    </VPopUpModal>

    <!-- Data Sharing Info Modal -->
    <VPopUpModal :modalStore="dataInfoModal" widthClass="w-3/4 max-w-4xl">
      <div>
        <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#3b88af]/10">
          <VIcon name="InfoCircleFilled" :size="28" class="text-[#3b88af]" />
        </div>
        <h3 class="mb-6 text-center text-xl font-bold text-gray-900 dark:text-gray-200">What Firms Can See</h3>

        <div class="mb-6">
          <div class="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">Shared Data</div>
          <div class="mb-4 grid grid-cols-2 gap-x-6">
            <div>
              <div class="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">Profile Information</div>
              <ul class="ml-4 list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>Name</li>
                <li>Email address</li>
                <li>Country</li>
                <li>LinkedIn profile URL</li>
                <li>Profile picture</li>
                <li>Accreditation status</li>
              </ul>
            </div>
            <div>
              <div class="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                Investment Preferences
              </div>
              <ul class="ml-4 list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>Investment types (Fund, Syndicate, Angel Group)</li>
                <li>Investment amount ranges</li>
                <li>Industries of interest</li>
                <li>University affiliations</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Table row preview -->
        <div v-if="previewInvestorCandidate" class="mb-6">
          <div class="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">Preview</div>
          <div
            class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-800"
          >
            <table class="w-full border-collapse">
              <thead>
                <tr>
                  <th
                    class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                  >
                    Name
                  </th>
                  <th
                    class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                  >
                    Email
                  </th>
                  <th
                    class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                  >
                    Country
                  </th>
                  <th
                    class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                  >
                    Status
                  </th>
                  <th
                    class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <ConnectionInvestorRow :candidate="previewInvestorCandidate" />
              </tbody>
            </table>
          </div>
        </div>

        <p
          class="mb-6 rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-500 dark:bg-gray-700 dark:text-gray-400"
        >
          Firms can view this information once you've been matched. You can update your profile at any time.
        </p>

        <button
          @click="dataInfoModal.close()"
          class="w-full rounded-lg bg-[#3b88af] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#3b88af]/90"
        >
          Got it
        </button>
      </div>
    </VPopUpModal>
  </ConnectionsLayout>
</template>

<style scoped>
/* Nav Bar */
.nav-bar {
  background: white;
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 8px;
}

:root.dark .nav-bar,
.dark .nav-bar {
  background: #1f2937;
  border-color: #374151;
}

.nav-tabs {
  display: flex;
  flex: 1;
}

.nav-tabs a {
  display: block;
  padding: 16px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  cursor: pointer;
}

.nav-tabs a:hover {
  color: #1a2332;
}

.nav-tabs a.active {
  color: #3b88af;
  border-bottom-color: #3b88af;
}

:root.dark .nav-tabs a,
.dark .nav-tabs a {
  color: #9ca3af;
}

:root.dark .nav-tabs a:hover,
.dark .nav-tabs a:hover {
  color: #e5e7eb;
}

:root.dark .nav-tabs a.active,
.dark .nav-tabs a.active {
  color: #3b88af;
  border-bottom-color: #3b88af;
}

/* Ghost Card */
.ghost-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  opacity: 0.6;
  pointer-events: none;
}

:root.dark .ghost-card,
.dark .ghost-card {
  background: #1f2937;
  border-color: #374151;
}

.ghost-logo {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #495057, #6c757d);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

/* Reject Button */
.btn-reject-card {
  padding: 9px;
  background: white;
  color: #e76f51;
  border: 1.5px solid rgba(231, 111, 81, 0.3);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-reject-card:hover {
  background: rgba(231, 111, 81, 0.05);
}

.btn-reject-card:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

:root.dark .btn-reject-card,
.dark .btn-reject-card {
  background: #1f2937;
}

/* Filter Chip */
.filter-chip {
  padding: 5px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  background: white;
  cursor: pointer;
  transition: all 0.15s;
}

.filter-chip:hover {
  border-color: #d1d5db;
}

.filter-chip.active {
  background: #3b88af;
  color: white;
  border-color: #3b88af;
}

:root.dark .filter-chip,
.dark .filter-chip {
  background: #374151;
  border-color: #4b5563;
  color: #9ca3af;
}

:root.dark .filter-chip.active,
.dark .filter-chip.active {
  background: #3b88af;
  color: white;
  border-color: #3b88af;
}

/* Pulse Ring Animation */
.pulse-ring {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(59, 136, 175, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.pulse-ring::before,
.pulse-ring::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(59, 136, 175, 0.2);
  animation: pulseRing 2s ease-out infinite;
}

.pulse-ring::after {
  animation-delay: 1s;
}

@keyframes pulseRing {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

/* Card Transition Animations */
.card-enter-active {
  transition: all 0.3s ease-out;
}

.card-leave-active {
  transition: all 0.3s ease-in;
}

.card-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.card-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.card-move {
  transition: transform 0.3s ease;
}
</style>
